import { useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import { MdEmail } from "react-icons/md";
import { FaWhatsapp, FaFacebook, FaLinkedin } from "react-icons/fa";
import { BsInstagram } from "react-icons/bs";
import useIsDesktop from "@/hooks/useIsDesktop";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import styles from "./ShareModal.module.css";

export default function ShareModal({ show, onHide, shareUrl, title }) {
  const [copied, setCopied] = useState(false);
  const { isDesktop } = useIsDesktop(900);

  useBodyScrollLock(show);

  useEffect(() => {
    if (!show) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onHide();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [show, onHide]);

  useEffect(() => {
    if (!show) setCopied(false);
  }, [show]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error("Fallback copy failed: ", fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleWhatsAppShare = () => {
    const text = encodeURIComponent(`${title || "Check out this package"}\n${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  };

  const handleFacebookShare = () => {
    const url = encodeURIComponent(shareUrl);
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleLinkedInShare = () => {
    const url = encodeURIComponent(shareUrl);
    const titleEncoded = encodeURIComponent(title || "");
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${url}&title=${titleEncoded}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleInstagramShare = async () => {
    await handleCopyLink();
    window.alert("Link copied! You can paste it in your Instagram post or story.");
  };

  const mailtoLink = `mailto:?subject=${encodeURIComponent(title || "Check out this package")}&body=${encodeURIComponent(`I thought you might be interested in this: ${shareUrl}`)}`;

  if (!show) return null;

  return (
    <div
      className={`${styles.root} ${isDesktop ? styles.rootDesktop : styles.rootMobile}`}
      role="dialog"
      aria-modal="true"
      aria-label="Share package"
    >
      <button type="button" className={styles.overlay} onClick={onHide} aria-label="Close share modal" />

      <div className={`${styles.modal} ${isDesktop ? styles.modalDesktop : styles.modalMobile}`}>
        <div className={styles.header}>
          <h2 className={styles.title}>Share with friends</h2>
          <button type="button" className={styles.closeBtn} onClick={onHide} aria-label="Close modal">
            <IoClose />
          </button>
        </div>

        <div className={styles.modalBody}>
            <div className={styles.sharingLinkSection}>
              <label className={styles.sharingLabel} htmlFor="share-link-input">
                Sharing Link
              </label>
              <div className={styles.linkContainer}>
                <input
                  id="share-link-input"
                  type="text"
                  value={shareUrl}
                  readOnly
                  className={styles.linkInput}
                  placeholder="Sharing link here"
                />
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className={styles.copyButton}
                  title={copied ? "Copied!" : "Copy link"}
                >
                  Copy Link
                </button>
              </div>
              {copied ? <span className={styles.copiedMessage}>Link copied!</span> : null}
            </div>

            <div className={styles.shareOptionsSection}>
              <h6 className={styles.shareOptionsTitle}>Or share on:</h6>
              <div className={styles.shareIcons}>
                <a href={mailtoLink} className={styles.shareIcon} title="Share via Email">
                  <MdEmail size={20} />
                </a>

                <button
                  type="button"
                  onClick={handleWhatsAppShare}
                  className={styles.shareIcon}
                  title="Share on WhatsApp"
                >
                  <FaWhatsapp size={20} />
                </button>

                <button
                  type="button"
                  onClick={handleFacebookShare}
                  className={styles.shareIcon}
                  title="Share on Facebook"
                >
                  <FaFacebook size={20} />
                </button>

                <button
                  type="button"
                  onClick={handleLinkedInShare}
                  className={styles.shareIcon}
                  title="Share on LinkedIn"
                >
                  <FaLinkedin size={20} />
                </button>

                <button
                  type="button"
                  onClick={handleInstagramShare}
                  className={styles.shareIcon}
                  title="Share on Instagram"
                >
                  <BsInstagram size={20} />
                </button>
              </div>
            </div>
          </div>
      </div>
    </div>
  );
}
